import { buildTransferIntelligence } from './mcTransferIntelligence.js';
import { buildTransferQuantityPreview } from './mcTransferQuantityPreview.js';
import { validateTransferRules } from './mcTransferRules.js';

// A provisional calculation assumes comparable units and zero incoming. It is
// never approved or executable; review uses the strict existing calculator.
export function buildTransferRecommendation({ rows = [], sourceScope = '', rules, review = null, evaluatedAt = '' } = {}) {
  const at = evaluatedAt || new Date().toISOString();
  const validRows = Array.isArray(rows) ? rows.filter((row) => row && typeof row === 'object') : [];
  const evidence = buildTransferIntelligence({ rows: validRows, sourceScope, evaluatedAt: at });
  let message = '';
  if (!evidence.sourceEligible) message = !evidence.source
    ? 'Current stock for this product is missing at this site.'
    : evidence.source.error || evidence.source.latestRevisionTrusted !== true
      ? 'The latest stock update at this site still needs checking.'
      : !(evidence.sourceSoh > 0)
        ? 'There is no stock to send from this site.'
        : evidence.sourceDays === null || evidence.sourceDays < 14 || evidence.source.networkSignal !== 'DEAD'
          ? 'We need a longer period without stock movement here before suggesting a transfer.'
          : 'Update the stock at this site before reviewing a transfer.';
  else if (!evidence.ranked.length) message = evidence.networkMoving
    ? 'Stock is moving elsewhere. We need more movement history to suggest an amount.'
    : 'No other site has enough recent movement to suggest a transfer.';
  const checked = validateTransferRules(rules);
  if (message) return { status: 'WAITING', evidence, message, checked: false, executionEnabled: false, proposal: null };
  if (!checked.policy) return { status: 'SETUP', evidence, message: 'Save your stock rules once to see suggested amounts.', checked: false, executionEnabled: false, proposal: null };
  const provisional = review === null;
  const destinationInputs = provisional
    ? Object.fromEntries(evidence.ranked.map((row) => [row.scope, { incoming: 0 }]))
    : review.destinationInputs || {};
  const proposal = buildTransferQuantityPreview({ rows: validRows, sourceScope, evaluatedAt: at,
    policy: { ...checked.policy, unitConfirmed: provisional || review.unitConfirmed === true }, destinationInputs });
  if (proposal.blockers.length) message = checked.policy.sourceReserve > Number(evidence.sourceSoh)
    ? 'The stock to keep is higher than the stock here. Check your stock rules.'
    : !provisional && review.unitConfirmed !== true
      ? 'Check that this product uses the same stock unit at each site.'
      : 'The stock information needs checking before we can suggest an amount.';
  else if (proposal.heldDestinations) message = 'Some sites still need a delivery or product check.';
  return { status: proposal.blockers.length ? 'WAITING' : 'SUGGESTION', evidence, message, proposal,
    checked: !provisional && !proposal.blockers.length && proposal.heldDestinations === 0,
    assumedNoIncoming: provisional, unitsChecked: !provisional && review.unitConfirmed === true,
    executionEnabled: false };
}

export const movementLabel = (signal) => ({ MOVING: 'Moving', SLOW: 'Slow', OBSERVED: 'Building history',
  DEAD: 'Not moving', OUT_OF_STOCK: 'Out of stock', ERROR: 'Unavailable', NO_EVIDENCE: 'No history yet' })[signal] || 'No history yet';
